import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import { first } from 'lodash-es';
import { Observable, of } from 'rxjs';

import { Article, IArticle } from 'article/article';
import { ModuleType } from 'module/model/module.type';
import { IModuleDependency, ModuleDependency } from 'module/module-dependency';
import { IPhase, Phase } from 'phase/phase';

import { IModuleColumn, ModuleColumn } from './column/module-column';
import { IModuleI18n, ModuleI18n } from './module-i18n';
import { IModulePage, ModulePage } from './page/module-page';

export interface IModule extends IEntity {
	readonly phaseId: string;
	readonly moduleId: string;
	readonly type: ModuleType;
	readonly position: number;
	readonly sectionId: string;
	readonly redirectUrl: string;
	readonly hasSettings: boolean;
	readonly pushModuleId: string;
	readonly organizationId: string;
	readonly isVisibleInPlan: boolean;
	readonly excludedQuestionsId: string[];
	readonly pagesId: string[];
	readonly articlesId: string[];
	readonly dependencies: IModuleDependency[];
	readonly columns: IModuleColumn[];
	readonly linkedGraphsId: string[];
	readonly linkedQuestionsId: string[];
	readonly localizations: IModuleI18n[];
	readonly phase$: Observable<IPhase>;
	readonly localization$: Observable<IModuleI18n>;
	readonly pages$: Observable<IModulePage[]>;
	readonly articles$: Observable<IArticle[]>;

	readonly linkRoot: string;
	readonly isPushModule: boolean;
	readonly isPitchModule: boolean;
	readonly isSynthesisModule: boolean;
	readonly isLinkedinModule: boolean;

	readonly canTriggerAnswersDocument: boolean;
	readonly averageSpentTime: string;
	readonly averageSpentDays: number;
	localization(language: string): IModuleI18n;
}

export class Module extends Entity implements IModule {
	private _phaseId: string;
	private _moduleId: string;
	private _type: ModuleType;
	private _position: number;
	private _sectionId: string;
	private _pagesId: string[];
	private _redirectUrl: string;
	private _articlesId: string[];
	private _hasSettings: boolean;
	private _pushModuleId: string;
	private _organizationId: string;
	private _columns: ModuleColumn[];
	private _linkedGraphsId: string[];
	private _isVisibleInPlan: boolean;
	private _localizations: ModuleI18n[];
	private _linkedQuestionsId: string[];
	private _excludedQuestionsId: string[];
	private _dependencies: ModuleDependency[];
	private _phase$: Observable<Phase>;
	private _pages$: Observable<ModulePage[]>;
	private _articles$: Observable<Article[]>;
	private _localization$: Observable<ModuleI18n>;
	private _canTriggerAnswersDocument: boolean;
	private _averageSpentTime: string;
	private _averageSpentDays: number;

	constructor(
		id: string,
		canTriggerAnswersDocument: boolean,
		averageSpentTime: string,
		averageSpentDays: number,
		phaseId: string,
		moduleId: string,
		type: ModuleType,
		position: number,
		pagesId: string[],
		sectionId: string,
		redirectUrl: string,
		articlesId: string[],
		hasSettings: boolean,
		pushModuleId: string,
		organizationId: string,
		columns: ModuleColumn[],
		updateTimestamp: number,
		isVisibleInPlan: boolean,
		linkedGraphsId: string[],
		localizations: ModuleI18n[],
		linkedQuestionsId: string[],
		excludedQuestionsId: string[],
		dependencies: ModuleDependency[],
		phase$: Observable<Phase> = of(null),
		pages$: Observable<ModulePage[]> = of([]),
		articles$: Observable<Article[]> = of([]),
		localization$: Observable<ModuleI18n> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._type = type;
		this._phaseId = phaseId;
		this._moduleId = moduleId;
		this._columns = columns;
		this._position = position;
		this._sectionId = sectionId;
		this._redirectUrl = redirectUrl;
		this._articlesId = articlesId;
		this._hasSettings = hasSettings;
		this._pushModuleId = pushModuleId;
		this._isVisibleInPlan = isVisibleInPlan;
		this._organizationId = organizationId;
		this._localizations = localizations;
		this._linkedGraphsId = linkedGraphsId;
		this._linkedQuestionsId = linkedQuestionsId;
		this._excludedQuestionsId = excludedQuestionsId;
		this._dependencies = dependencies;
		this._pagesId = pagesId;
		this._phase$ = phase$;
		this._localization$ = localization$;
		this._pages$ = pages$;
		this._articles$ = articles$;
		this._canTriggerAnswersDocument = canTriggerAnswersDocument;
		this._averageSpentTime = averageSpentTime;
		this._averageSpentDays = averageSpentDays;
	}

	public get phaseId(): string {
		return this._phaseId;
	}

	public get canTriggerAnswersDocument(): boolean {
		return this._canTriggerAnswersDocument;
	}

	public get averageSpentTime(): string {
		return this._averageSpentTime;
	}

	public get averageSpentDays(): number {
		return this._averageSpentDays;
	}

	public get moduleId(): string {
		return this._moduleId;
	}

	public get type(): ModuleType {
		return this._type;
	}

	public get position(): number {
		return this._position;
	}

	public get sectionId(): string {
		return this._sectionId;
	}

	public get redirectUrl(): string {
		return this._redirectUrl;
	}

	public get hasSettings(): boolean {
		return this._hasSettings;
	}

	public get pushModuleId(): string {
		return this._pushModuleId;
	}

	public get organizationId(): string {
		return this._organizationId;
	}

	public get isVisibleInPlan(): boolean {
		return this._isVisibleInPlan;
	}

	public get columns(): ModuleColumn[] {
		return this._columns;
	}

	public get linkedGraphsId(): string[] {
		return this._linkedGraphsId;
	}

	public get localizations(): ModuleI18n[] {
		return this._localizations;
	}

	public get linkedQuestionsId(): string[] {
		return this._linkedQuestionsId;
	}

	public get excludedQuestionsId(): string[] {
		return this._excludedQuestionsId;
	}

	public get pagesId(): string[] {
		return this._pagesId;
	}

	public get articlesId(): string[] {
		return this._articlesId;
	}

	public get dependencies(): ModuleDependency[] {
		return this._dependencies;
	}

	public get phase$(): Observable<IPhase> {
		return this._phase$;
	}

	public get localization$(): Observable<ModuleI18n> {
		return this._localization$;
	}

	public get pages$(): Observable<ModulePage[]> {
		return this._pages$;
	}

	public get articles$(): Observable<Article[]> {
		return this._articles$;
	}

	public get isPushModule(): boolean {
		return this._type === ModuleType.PUSH;
	}

	public get isPitchModule(): boolean {
		return this._type === ModuleType.PITCH;
	}

	public get isSynthesisModule(): boolean {
		return this._type === ModuleType.SYNTHESIS;
	}

	public get isLinkedinModule(): boolean {
		return this._type === ModuleType.LINKEDIN;
	}

	public get linkRoot(): string {
		switch (this._type) {
			case ModuleType.MOSAIC:
				return '/mosaic';
			case ModuleType.CONTACT_KANBAN:
				return '/kanban-networking';
			case ModuleType.JOB_KANBAN:
				return '/kanban-job-offer';
			default:
				return '/activity';
		}
	}

	localization(language: string): IModuleI18n {
		return (
			this._localizations.find((l) => l.locale === language) ??
			first(this._localizations)
		);
	}
}
